import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * Renders compact "you may also like" product recommendations inside the cart drawer.
 *
 * Registered as a custom element (rather than relying on an inline <script>) because the cart
 * drawer's markup is refreshed via DOM morphing after every cart change. A morphed-in <script>
 * tag never executes, and morphing also clears this element's previously rendered cards (the
 * server-rendered markup for this element is always an empty grid), so recommendations must be
 * (re)loaded from `connectedCallback` and on every cart update rather than once at parse time.
 */
class CartRecommendationsComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #grid = null;

  /** @type {HTMLTemplateElement | null} */
  #closeIconTemplate = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    this.#grid = this.querySelector('.cart-recommendations__grid');
    this.#closeIconTemplate = this.querySelector('.cart-rec-close-icon-template');
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#activeFetch?.abort();
  }

  #handleCartUpdate = () => {
    // Let any synchronous section morph triggered by the same event (which may update this
    // element's data-product-id to the new last-added item) settle before reading it.
    queueMicrotask(() => this.#load());
  };

  async #load() {
    const { productId, limit: limitAttr, addLabel, selectSizeLabel, closeLabel, currencyCode } = this.dataset;
    const limit = Number(limitAttr) || 2;

    if (!productId || !this.#grid) return;

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    let products = [];
    try {
      products = await this.#fetchRecommendations(productId, limit, 'complementary', controller.signal);
      if (products.length === 0) {
        products = await this.#fetchRecommendations(productId, limit, 'related', controller.signal);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      this.hidden = true;
      return;
    }

    if (controller.signal.aborted) return;

    // Only recommend products that have at least one purchasable variant: a sold-out size
    // shouldn't be upsold, and #buildCard needs a variant to default the displayed price to.
    products = products.filter((product) => product.variants.some((variant) => variant.available));

    if (products.length === 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#grid.innerHTML = '';
    products.slice(0, limit).forEach((product) => {
      this.#grid?.appendChild(this.#buildCard(product, addLabel, selectSizeLabel, closeLabel, currencyCode));
    });
  }

  /**
   * @param {string} productId
   * @param {number} limit
   * @param {string} intent
   * @param {AbortSignal} signal
   */
  async #fetchRecommendations(productId, limit, intent, signal) {
    const response = await fetch(
      `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`,
      { signal }
    );
    if (!response.ok) throw new Error('Fetch failed');
    const data = await response.json();
    return data.products || [];
  }

  /**
   * @param {number} variantId
   * @param {HTMLButtonElement} btn
   * @param {string} addLabel
   */
  async #addToCart(variantId, btn, addLabel) {
    btn.disabled = true;
    btn.textContent = '...';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      if (!response.ok) throw new Error();

      document.dispatchEvent(new CartAddEvent({}, this.id || 'cart-recommendations', { source: 'cart-recommendations' }));

      btn.textContent = '✓';
      setTimeout(() => {
        btn.textContent = addLabel;
        btn.disabled = false;
      }, 1800);
    } catch {
      btn.textContent = '!';
      btn.disabled = false;
    }
  }

  /**
   * @param {number} cents
   * @param {string} currencyCode
   */
  #formatPrice(cents, currencyCode) {
    return (cents / 100).toLocaleString('en', {
      style: 'currency',
      currency: currencyCode,
    });
  }

  /**
   * Trims the merchandising suffix off a product title, matching the theme's
   * `title | split: '|' | first | strip` convention used in the Liquid templates.
   *
   * @param {string} title
   */
  #shortTitle(title) {
    return title.split('|')[0].trim();
  }

  /**
   * Reveals the size-picker overlay for a card and moves focus to its first swatch.
   *
   * @param {HTMLElement} overlay
   */
  #openSizeOverlay(overlay) {
    overlay.hidden = false;
    const firstSwatch = /** @type {HTMLElement | null} */ (overlay.querySelector('.cart-rec-card__size-swatch'));
    firstSwatch?.focus();
  }

  /**
   * @param {HTMLElement} overlay
   * @param {HTMLElement} returnFocusTo - The "Add" button that opened the overlay.
   */
  #closeSizeOverlay(overlay, returnFocusTo) {
    overlay.hidden = true;
    returnFocusTo.focus();
  }

  /**
   * @param {any} product
   * @param {string} addLabel
   * @param {string} selectSizeLabel
   * @param {string} closeLabel
   * @param {string} currencyCode
   */
  #buildCard(product, addLabel, selectSizeLabel, closeLabel, currencyCode) {
    // Sold-out sizes aren't offered: adding one would 422 at /cart/add.js, and there'd be no
    // way for the shopper to tell which size they were about to get.
    const availableVariants = product.variants.filter((/** @type {any} */ v) => v.available);
    const variant = availableVariants[0];
    const imgSrc = product.featured_image ? product.featured_image + '&width=120' : null;

    const card = document.createElement('div');
    card.className = 'cart-rec-card';
    card.setAttribute('role', 'listitem');

    const imgLink = document.createElement('a');
    imgLink.className = 'cart-rec-card__image-link';
    imgLink.href = product.url;
    imgLink.tabIndex = -1;
    imgLink.setAttribute('aria-hidden', 'true');
    if (imgSrc) {
      const img = document.createElement('img');
      img.className = 'cart-rec-card__image';
      img.src = imgSrc;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      imgLink.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'cart-rec-card__info';

    const shortTitle = this.#shortTitle(product.title);

    const titleLink = document.createElement('a');
    titleLink.className = 'cart-rec-card__title cart-primary-typography';
    titleLink.href = product.url;
    titleLink.textContent = shortTitle;

    const hasCompareAt = variant.compare_at_price && variant.compare_at_price > variant.price;

    const prices = document.createElement('div');
    prices.className = 'cart-rec-card__prices cart-secondary-typography';

    const priceSpan = document.createElement('span');
    priceSpan.className = 'cart-rec-card__price';
    priceSpan.textContent = this.#formatPrice(variant.price, currencyCode);
    prices.appendChild(priceSpan);

    if (hasCompareAt) {
      const compareAt = document.createElement('s');
      compareAt.className = 'cart-rec-card__compare-at';
      compareAt.textContent = this.#formatPrice(variant.compare_at_price, currencyCode);
      prices.appendChild(compareAt);
    }

    info.appendChild(titleLink);
    info.appendChild(prices);

    const btn = document.createElement('button');
    btn.className = 'cart-rec-card__atc button button--primary';
    btn.type = 'button';
    btn.setAttribute('aria-label', addLabel + ': ' + shortTitle);
    btn.textContent = addLabel;

    card.appendChild(imgLink);
    card.appendChild(info);
    card.appendChild(btn);

    // A size is only offered a choice when there's more than one in stock — a single available
    // variant (or a product with no real size option) is added directly, matching prior behavior.
    if (availableVariants.length > 1) {
      const overlay = this.#buildSizeOverlay(product, availableVariants, btn, addLabel, selectSizeLabel, closeLabel);
      card.appendChild(overlay);
      btn.addEventListener('click', () => this.#openSizeOverlay(overlay));
    } else {
      btn.addEventListener('click', () => this.#addToCart(variant.id, btn, addLabel));
    }

    return card;
  }

  /**
   * Builds the size-picker overlay shown over a card when its "Add" button is clicked: a
   * PDP-style row of size swatches that closes and adds to cart as soon as one is picked.
   *
   * @param {any} product
   * @param {any[]} availableVariants
   * @param {HTMLButtonElement} addButton
   * @param {string} addLabel
   * @param {string} selectSizeLabel
   * @param {string} closeLabel
   */
  #buildSizeOverlay(product, availableVariants, addButton, addLabel, selectSizeLabel, closeLabel) {
    const overlay = document.createElement('div');
    overlay.className = 'cart-rec-card__size-overlay';
    overlay.hidden = true;

    const header = document.createElement('div');
    header.className = 'cart-rec-card__size-overlay-header';

    const title = document.createElement('span');
    title.className = 'cart-rec-card__size-overlay-title';
    title.textContent = selectSizeLabel;
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'button button-unstyled close-button';
    closeBtn.setAttribute('aria-label', closeLabel);
    if (this.#closeIconTemplate) {
      closeBtn.appendChild(this.#closeIconTemplate.content.cloneNode(true));
    }
    closeBtn.addEventListener('click', () => this.#closeSizeOverlay(overlay, addButton));
    header.appendChild(closeBtn);

    const swatches = document.createElement('div');
    swatches.className = 'cart-rec-card__size-swatches';
    swatches.setAttribute('role', 'list');

    availableVariants.forEach((/** @type {any} */ v) => {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'cart-rec-card__size-swatch';
      swatch.textContent = v.title;
      swatch.setAttribute('aria-label', v.title + ': ' + this.#shortTitle(product.title));
      swatch.addEventListener('click', () => {
        this.#closeSizeOverlay(overlay, addButton);
        this.#addToCart(v.id, addButton, addLabel);
      });
      swatches.appendChild(swatch);
    });

    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeSizeOverlay(overlay, addButton);
    });

    overlay.appendChild(header);
    overlay.appendChild(swatches);

    return overlay;
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
